import React, { useEffect, useMemo, useState } from 'react';
import { Calendar } from 'react-big-calendar';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { AgendaEventField, gyLocalizer, Spinner } from '@guryou/html-components';
import { StateStatus, unifiedBookingsAction, useCalendarMessages, useCheckIsMobile } from '@guryou/redux';
import { momentToDate, parseDate } from '@guryou/utils';
import moment from 'moment';
import { normalizeColor } from './utils';
import { initFilterState } from '../../../components/calendar/CalendarFilter';
import { useWidgetAuthLockedProvider } from '../../../hooks/useWidgetAuthLockedProvider';
import CustomToolbar from '../../../components/calendar/CustomToolbar';
import EventField from '../../../components/calendar/EventField';
import FilterButton from '../../../components/calendar/FilterButton';
import MonthEventField from '../../../components/calendar/MonthEventField';
import ReservationDetails from '../../../components/calendar/ReservationDetails';
import Icon from '../../../components/shared/Icon';
import Modal from '../../../components/shared/Modal';
import '../../../assets/react-big-calendar.css';

const localizer = gyLocalizer(moment);

const ReservationsCalendar = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const calendarMessages = useCalendarMessages();
  const isMobile = useCheckIsMobile();
  const [selectedPeriod, setSelectedPeriod] = useState(parseDate(null));
  const [defaultCalendarView, setDefaultCalendarView] = useState('month');
  const [filterState, setFilterState] = useState(initFilterState);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const unifiedBookings = useSelector(state => state.calendar?.unifiedBookings);
  const widgetIntegration = useSelector(state => state.widget?.integration);

  const hasProviderSelected = filterState.providerId && filterState.providerId !== 'all';
  const selectedProviderId = hasProviderSelected ? parseInt(filterState.providerId) : null;

  const calendarData = unifiedBookings;
  const isLoading = calendarData?.status === StateStatus.LOADING;
  const hasData = calendarData?.data && Array.isArray(calendarData.data);
  const unifiedBookingsData = unifiedBookings?.data;
  const providerOptions = useMemo(() => {
    if (!unifiedBookingsData || !Array.isArray(unifiedBookingsData)) {
      return [{ value: 'all', label: t('GENERAL.all', 'Todos') }];
    }

    const providersMap = new Map();

    unifiedBookingsData.forEach(event => {
      if (event && event.providerId) {
        const providerId = event.providerId.toString();
        const providerName = event.providerName || event.provider_name || event.provider?.name || `Provider ${event.providerId}`;

        if (!providersMap.has(providerId)) {
          providersMap.set(providerId, {
            value: providerId,
            label: providerName,
          });
        }
      }
    });

    return [{ value: 'all', label: t('GENERAL.all', 'Todos') }, ...Array.from(providersMap.values())];
  }, [unifiedBookingsData, t]);

  const lockProviderId = useWidgetAuthLockedProvider();
  const lockProviderName = widgetIntegration?.data?.provider?.name || widgetIntegration?.data?.name || null;

  const centerDisplay = useMemo(() => {
    const selectableOptions = providerOptions.filter(option => option.value !== 'all');
    const activeProviderId = lockProviderId || filterState.providerId || 'all';
    const isAllCenters = !lockProviderId && activeProviderId === 'all';
    const label = isAllCenters
      ? t('CALENDAR.allCenters', 'Tutti i centri')
      : lockProviderName || providerOptions.find(option => option.value === activeProviderId)?.label || selectableOptions[0]?.label || null;

    return label ? { label, isAllCenters } : null;
  }, [providerOptions, filterState.providerId, lockProviderId, lockProviderName, t]);

  useEffect(() => {
    if (!lockProviderId) {
      return;
    }
    setFilterState(prev => {
      if (prev.providerId === lockProviderId) {
        return prev;
      }
      return { ...prev, providerId: lockProviderId };
    });
  }, [lockProviderId]);

  useEffect(() => {
    const entityType = filterState.entityType && filterState.entityType !== 'all' ? parseInt(filterState.entityType) : undefined;
    const providerId = hasProviderSelected ? selectedProviderId : undefined;
    dispatch(unifiedBookingsAction(providerId, selectedPeriod.toISOString(), entityType));
  }, [dispatch, hasProviderSelected, selectedProviderId, selectedPeriod, filterState.entityType]);

  const events = useMemo(() => {
    if (!hasData) {
      return [];
    }

    let data = [];
    if (unifiedBookings?.data) {
      data = Array.isArray(unifiedBookings.data) ? unifiedBookings.data : unifiedBookings.data.response || unifiedBookings.data || [];
    }

    return data.map(event => {
      let eventColor = null;

      if (unifiedBookings?.data) {
        if (event.type === 'reservation') {
          eventColor = '#3B82F6';
        } else if (event.type === 'subscription') {
          if (event.waitingListPosition != null && event.waitingListPosition > 0) {
            eventColor = '#F97316';
          } else if (event.isSessionBought === true || event.subscriptionId != null) {
            eventColor = '#10B981';
          } else {
            eventColor = '#E5E7EB';
          }
        }
      }

      const finalColor = eventColor ? eventColor : normalizeColor(event.color, event.entityType);

      const eventWithProvider = {
        ...event,
        color: finalColor,
        providerName: event.providerName || event.provider_name,
      };

      return eventWithProvider;
    });
  }, [unifiedBookings, hasData]);

  const views = isMobile ? ['day', 'agenda'] : ['month', 'week', 'day', 'agenda'];
  const calendarCulture = i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'it';

  useEffect(() => {
    moment.locale(calendarCulture);
  }, [calendarCulture]);

  useEffect(() => {
    const body = document.body;

    body.style.overflowY = 'hidden';
    body.style.height = '100vh';
    body.style.maxHeight = '100vh';

    return () => {
      body.style.overflowY = '';
      body.style.height = '';
      body.style.maxHeight = '';
    };
  }, []);

  return (
    <>
      <div className="flex flex-col h-full w-full overflow-hidden">
        <div
          className="bg-white rounded-xl shadow-lg border border-gray-200 flex flex-col h-full"
          style={{ padding: '24px', boxSizing: 'border-box' }}
        >
          <div className="mb-4 pb-4 border-b border-gray-200" style={{ flexShrink: 0 }}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <div
                  className="p-2 rounded-lg"
                  style={{
                    backgroundColor: 'rgba(var(--feel-primary-rgb), 0.18)',
                  }}
                >
                  <svg className="w-6 h-6 text-feel-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{t('CALENDAR.title', 'Calendario')}</h2>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {events.length} {events.length === 1 ? t('GENERAL.event', 'evento') : t('GENERAL.events', 'eventi')}
                  </p>
                </div>
              </div>

              <FilterButton
                filterState={filterState}
                setFilterState={setFilterState}
                selectedPeriod={selectedPeriod}
                setSelectedPeriod={setSelectedPeriod}
                setDefaultCalendarView={setDefaultCalendarView}
                providerOptions={providerOptions}
                lockProviderId={lockProviderId}
              />
            </div>
          </div>

          <div style={{ flex: '1 1 auto', minHeight: 0, position: 'relative', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {(isLoading || !hasData) && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                }}
              >
                <Spinner />
              </div>
            )}
            {events.length === 0 && hasData ? (
              <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                <div className="bg-gray-50 rounded-full p-6 mb-6">
                  <svg className="w-20 h-20 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('RESERVATIONS.noReservations', 'Non ci sono prenotazioni.')}</h3>
                <p className="text-gray-500 text-sm max-w-md">
                  {t('RESERVATIONS.tryDifferentFilters', 'Prova a modificare i filtri per vedere più risultati.')}
                </p>
              </div>
            ) : (
              <div style={{ flex: '1 1 auto', minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                <Calendar
                  style={{ flex: '1 1 auto', minHeight: 0, overflow: 'hidden' }}
                  culture={calendarCulture}
                  localizer={localizer}
                  events={events}
                  view={isMobile && defaultCalendarView !== 'day' && defaultCalendarView !== 'agenda' ? 'day' : defaultCalendarView}
                  onView={v => setDefaultCalendarView(v)}
                  views={views}
                  step={15}
                  scrollToTime={new Date()}
                  showMultiDayTimes
                  startAccessor={event => momentToDate(event.startTime)}
                  endAccessor={event => momentToDate(event.endTime)}
                  messages={calendarMessages}
                  onNavigate={ev => {
                    const date = parseDate(ev);
                    const previousPeriod = selectedPeriod;
                    setSelectedPeriod(date);
                    if (date.month() !== previousPeriod.month() || date.year() !== previousPeriod.year()) {
                      const entityType = filterState.entityType && filterState.entityType !== 'all' ? parseInt(filterState.entityType) : undefined;
                      const providerId = hasProviderSelected ? selectedProviderId : undefined;
                      dispatch(unifiedBookingsAction(providerId, date.toISOString(), entityType));
                    }
                  }}
                  date={selectedPeriod.toDate()}
                  onSelectEvent={event => {
                    setSelectedEvent(event);
                    setIsModalOpen(true);
                  }}
                  components={{
                    toolbar: props => <CustomToolbar {...props} centerLabel={centerDisplay?.label} isAllCenters={centerDisplay?.isAllCenters} />,
                    event: ({ event }) => <EventField event={event} namePreset="username" t={t} />,
                    month: {
                      event: ({ event }) => <MonthEventField event={event} namePreset="username" />,
                    },
                    agenda: {
                      event: ({ event }) => (
                        <AgendaEventField
                          event={event}
                          descriptions={{
                            eventName: t('GENERAL.event', 'Evento'),
                            email: t('GENERAL.email'),
                            resource: t('PROFILE.resource'),
                          }}
                        />
                      ),
                    },
                  }}
                  popup
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedEvent(null);
        }}
        headerContent={
          selectedEvent?.name ? (
            <div className="flex-1">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 pt-1">
                  <Icon name="calendar" size="lg" color="#4F46E5" />
                </div>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold text-gray-900">{selectedEvent.name}</h2>
                  {selectedEvent.description && <p className="text-sm text-gray-600 mt-1">{selectedEvent.description}</p>}
                </div>
              </div>
            </div>
          ) : (
            <h2 className="text-2xl font-bold text-gray-900">
              {selectedEvent?.serviceName || t('RESERVATIONS.reservationDetails', 'Detalhes da Reserva')}
            </h2>
          )
        }
      >
        <ReservationDetails event={selectedEvent} />
      </Modal>
    </>
  );
};

export default ReservationsCalendar;
